import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, 
  Plus, 
  Calendar,
  Settings,
  Trash2,
  Edit,
  Eye,
  Loader2,
  CheckCircle,
  Clock,
  Archive,
  Mail,
  Download,
  UserPlus,
  UserMinus,
  BarChart3,
  PieChart,
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Copy,
  Check,
  Send,
  FileText,
  Filter
} from 'lucide-react';

const STATUS_CONFIG = {
  active: { color: 'bg-emerald-100 text-emerald-800', label: 'Active' },
  completed: { color: 'bg-blue-100 text-blue-800', label: 'Completed' },
  archived: { color: 'bg-gray-100 text-gray-800', label: 'Archived' }
};

// Simple bar chart component
function SimpleBarChart({ data, title, colorClass = 'bg-primary' }) {
  const maxValue = Math.max(...Object.values(data), 1);
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground">{title}</h4>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No data available</p>
      ) : (
        entries.map(([label, value]) => (
          <div key={label} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="truncate max-w-[200px]">{label}</span>
              <span className="font-medium">{value}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${(value / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function AdminCohortDetail() {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [cohort, setCohort] = useState(null);
  const [members, setMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [availableApplicants, setAvailableApplicants] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Dialogs
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCapacityWarning, setShowCapacityWarning] = useState(false);
  const [pendingMemberAdd, setPendingMemberAdd] = useState(null);
  
  // Form states
  const [selectedApplicant, setSelectedApplicant] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // New: Filter and email mode states
  const [applicantFilter, setApplicantFilter] = useState('accepted');
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [useDirectEmail, setUseDirectEmail] = useState(false);
  const [directEmailBody, setDirectEmailBody] = useState('');
  
  useEffect(() => {
    fetchCohortData();
  }, [cohortId]);
  
  // Fetch applicants when filter changes
  useEffect(() => {
    fetchAvailableApplicants();
  }, [applicantFilter, showAllParticipants]);
  
  const fetchCohortData = async () => {
    try {
      setLoading(true);
      
      // First, fetch the critical cohort data - this must succeed
      let cohortRes;
      try {
        cohortRes = await axios.get(`/api/admin/cohorts/${cohortId}`);
      } catch (error) {
        console.error('Failed to fetch cohort:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Failed to load cohort: ${errorMsg}`);
        setLoading(false);
        return; // Exit early if we can't get the cohort
      }
      
      // Set cohort data immediately
      setCohort(cohortRes.data.cohort);
      setMembers(cohortRes.data.members || []);
      
      // Initialize edit form
      setEditForm({
        name: cohortRes.data.cohort.name,
        description: cohortRes.data.cohort.description || '',
        capacity: cohortRes.data.cohort.capacity,
        status: cohortRes.data.cohort.status,
        startDate: cohortRes.data.cohort.startDate ? cohortRes.data.cohort.startDate.split('T')[0] : '',
        endDate: cohortRes.data.cohort.endDate ? cohortRes.data.cohort.endDate.split('T')[0] : ''
      });
      
      // Now fetch secondary data - these can fail independently
      const secondaryPromises = [
        axios.get(`/api/admin/cohorts/${cohortId}/analytics`).catch(e => {
          console.warn('Failed to fetch analytics:', e);
          return { data: null };
        }),
        axios.get(`/api/admin/cohorts/available-applicants?filter_type=${applicantFilter}&include_assigned=${showAllParticipants}`).catch(e => {
          console.warn('Failed to fetch applicants:', e);
          return { data: { applicants: [] } };
        }),
        axios.get('/api/admin/email-templates').catch(e => {
          console.warn('Failed to fetch email templates:', e);
          return { data: { templates: [] } };
        })
      ];
      
      const [analyticsRes, applicantsRes, templatesRes] = await Promise.all(secondaryPromises);
      
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
      setAvailableApplicants(applicantsRes.data.applicants || []);
      setEmailTemplates(templatesRes.data.templates || []);
      
    } catch (error) {
      console.error('Unexpected error in fetchCohortData:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableApplicants = async () => {
    try {
      const response = await axios.get(
        `/api/admin/cohorts/available-applicants?filter_type=${applicantFilter}&include_assigned=${showAllParticipants}`
      );
      setAvailableApplicants(response.data.applicants || []);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    }
  };
  
  const addMember = async (forceAdd = false) => {
    if (!selectedApplicant) {
      toast.error('Please select an applicant');
      return;
    }
    
    setAddingMember(true);
    try {
      const response = await axios.post(`/api/admin/cohorts/${cohortId}/members`, {
        userId: selectedApplicant,
        forceAdd: forceAdd
      });
      
      // Check for capacity warning
      if (response.data.warning) {
        setPendingMemberAdd(selectedApplicant);
        setShowCapacityWarning(true);
        setAddingMember(false);
        return;
      }
      
      // Success
      const successMessage = response.data.capacityExceeded 
        ? 'Member added (capacity exceeded)' 
        : 'Member added to cohort';
      toast.success(successMessage);
      
      setShowAddMemberDialog(false);
      setSelectedApplicant('');
      fetchCohortData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };
  
  const confirmCapacityOverride = async () => {
    if (!pendingMemberAdd) return;
    
    setShowCapacityWarning(false);
    setAddingMember(true);
    
    try {
      await axios.post(`/api/admin/cohorts/${cohortId}/members`, {
        userId: pendingMemberAdd,
        forceAdd: true
      });
      
      toast.success('Member added (capacity exceeded)');
      setShowAddMemberDialog(false);
      setSelectedApplicant('');
      setPendingMemberAdd(null);
      fetchCohortData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };
  
  const removeMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this cohort?`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/cohorts/${cohortId}/members/${userId}`);
      toast.success('Member removed');
      fetchCohortData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };
  
  const sendCohortEmail = async () => {
    if (useDirectEmail) {
      // Direct email validation
      if (!emailSubject.trim()) {
        toast.error('Please enter an email subject');
        return;
      }
      if (!directEmailBody.trim()) {
        toast.error('Please enter the email body');
        return;
      }
    } else {
      // Template email validation
      if (!selectedTemplate) {
        toast.error('Please select an email template');
        return;
      }
    }
    
    setSendingEmail(true);
    try {
      const payload = useDirectEmail 
        ? {
            useDirectEmail: true,
            subject: emailSubject,
            directEmailBody: directEmailBody,
            customMessage: customMessage
          }
        : {
            templateId: selectedTemplate,
            subject: emailSubject || undefined,
            customMessage: customMessage
          };
      
      const response = await axios.post(`/api/admin/cohorts/${cohortId}/send-email`, payload);
      
      toast.success(`Email sent to ${response.data.sentCount} members`);
      if (response.data.failedCount > 0) {
        toast.warning(`${response.data.failedCount} emails failed to send`);
      }
      
      setShowEmailDialog(false);
      resetEmailForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };
  
  const resetEmailForm = () => {
    setSelectedTemplate('');
    setEmailSubject('');
    setCustomMessage('');
    setDirectEmailBody('');
    setUseDirectEmail(false);
  };
  
  const updateCohort = async () => {
    setSaving(true);
    try {
      // Format dates properly for the backend
      const formData = {
        ...editForm,
        // Convert YYYY-MM-DD to ISO format if dates are provided
        startDate: editForm.startDate ? `${editForm.startDate}T00:00:00Z` : undefined,
        endDate: editForm.endDate ? `${editForm.endDate}T23:59:59Z` : undefined
      };
      
      await axios.put(`/api/admin/cohorts/${cohortId}`, formData);
      toast.success('Cohort updated successfully');
      setShowEditDialog(false);
      fetchCohortData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update cohort');
    } finally {
      setSaving(false);
    }
  };
  
  const exportCohort = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const response = await axios.get(`/api/admin/cohorts/${cohortId}/export?format=csv`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${cohort?.name || 'cohort'}_export.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await axios.get(`/api/admin/cohorts/${cohortId}/export?format=json`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${cohort?.name || 'cohort'}_export.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export cohort data');
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!cohort) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertTriangle className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Cohort not found</h2>
        <Button onClick={() => navigate('/admin/cohorts')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cohorts
        </Button>
      </div>
    );
  }
  
  const statusConfig = STATUS_CONFIG[cohort.status] || STATUS_CONFIG.active;
  const memberCount = members.length;
  const capacity = cohort.capacity || 20;
  const capacityPercentage = (memberCount / capacity) * 100;
  const isOverCapacity = memberCount > capacity;
  const isAtCapacity = memberCount >= capacity;
  
  return (
    <div data-testid="cohort-detail-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin/cohorts')} 
            className="text-muted-foreground hover:text-foreground text-sm flex items-center gap-1"
            data-testid="back-to-cohorts-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cohorts
          </button>
          <div className="h-6 w-px bg-border" />
          <div>
            <h1 className="font-serif text-xl font-semibold flex items-center gap-2">
              {cohort.name}
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            </h1>
            {cohort.description && (
              <p className="text-sm text-muted-foreground mt-1">{cohort.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowEditDialog(true)}
            data-testid="edit-cohort-btn"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchCohortData()}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Members</p>
                  <p className="text-2xl font-bold">{memberCount} / {capacity}</p>
                </div>
                <Users className="w-8 h-8 text-primary opacity-80" />
              </div>
              <Progress 
                value={Math.min(capacityPercentage, 100)} 
                className={`h-2 mt-3 ${isOverCapacity ? '[&>div]:bg-destructive' : isAtCapacity ? '[&>div]:bg-warning' : ''}`}
              />
              {isOverCapacity && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Over capacity
                </p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">EDGE Diagnostic</p>
                  <p className="text-2xl font-bold">
                    {analytics?.completionStatus?.fearPercentage || 0}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-orange-500 opacity-80" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {analytics?.completionStatus?.fearComplete || 0} of {memberCount} completed
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">CORE Diagnostic</p>
                  <p className="text-2xl font-bold">
                    {analytics?.completionStatus?.archetypePercentage || 0}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-purple-500 opacity-80" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {analytics?.completionStatus?.archetypeComplete || 0} of {memberCount} completed
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Both Complete</p>
                  <p className="text-2xl font-bold">
                    {analytics?.completionStatus?.bothPercentage || 0}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500 opacity-80" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {analytics?.completionStatus?.bothComplete || 0} fully completed
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 mb-6">
          <Button onClick={() => setShowAddMemberDialog(true)} data-testid="add-member-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowEmailDialog(true)}
            disabled={memberCount === 0}
            data-testid="send-email-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send Email
          </Button>
          <div className="flex items-center gap-1 ml-auto">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportCohort('csv')}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportCohort('json')}
              disabled={exporting}
              data-testid="export-json-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              JSON
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="space-y-6">
          <Tabs defaultValue="members">
            <TabsList>
              <TabsTrigger value="members" data-testid="members-tab">
                <Users className="w-4 h-4 mr-2" />
                Members ({memberCount})
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="analytics-tab">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>
            
            {/* Members Tab */}
            <TabsContent value="members">
              <Card>
                <CardHeader>
                  <CardTitle>Cohort Members</CardTitle>
                  <CardDescription>
                    Manage participants in this cohort
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {members.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <h3 className="font-semibold mb-2">No members yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Add participants to this cohort to get started
                      </p>
                      <Button onClick={() => setShowAddMemberDialog(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add First Member
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>EDGE Diagnostic</TableHead>
                          <TableHead>CORE Archetype</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map(member => (
                          <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                            <TableCell className="font-medium">
                              {member.firstName} {member.lastName}
                            </TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{member.company || '-'}</TableCell>
                            <TableCell>
                              {member.fearAssessment?.completed ? (
                                <div>
                                  <Badge variant="outline" className="bg-orange-50">
                                    {member.fearAssessment.primaryFear}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground ml-2">
                                    {member.fearAssessment.intensity}
                                  </span>
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {member.archetypeAssessment?.completed ? (
                                <Badge variant="outline" className="bg-purple-50">
                                  {member.archetypeAssessment.primaryArchetype}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/admin/participants/${member.id}`)}
                                  data-testid={`view-member-${member.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeMember(member.id, `${member.firstName} ${member.lastName}`)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`remove-member-${member.id}`}
                                >
                                  <UserMinus className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Edge Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-orange-600" />
                      Edge Distribution
                    </CardTitle>
                    <CardDescription>
                      Primary pressure patterns across cohort members
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={analytics?.fearDistribution || {}}
                      title="Primary Edges"
                      colorClass="bg-orange-500"
                    />
                  </CardContent>
                </Card>
                
                {/* Intensity Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-red-600" />
                      Edge Intensity
                    </CardTitle>
                    <CardDescription>
                      Distribution of pressure intensity levels
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={analytics?.intensityDistribution || {}}
                      title="Intensity Levels"
                      colorClass="bg-red-500"
                    />
                  </CardContent>
                </Card>
                
                {/* CORE Archetype Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-violet-600" />
                      CORE Archetype Distribution
                    </CardTitle>
                    <CardDescription>
                      Primary archetypes across cohort members
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={analytics?.archetypeDistribution || {}}
                      title="Primary Archetypes"
                      colorClass="bg-violet-500"
                    />
                  </CardContent>
                </Card>
                
                {/* Secondary Archetype Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                      Secondary Archetypes
                    </CardTitle>
                    <CardDescription>
                      Secondary archetype distribution
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={analytics?.secondaryArchetypeDistribution || {}}
                      title="Secondary Archetypes"
                      colorClass="bg-indigo-500"
                    />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      
      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Member to Cohort</DialogTitle>
            <DialogDescription>
              Select a participant to add to this cohort
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Filter Controls */}
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              <Select value={applicantFilter} onValueChange={setApplicantFilter}>
                <SelectTrigger className="w-[180px]" data-testid="applicant-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accepted Only</SelectItem>
                  <SelectItem value="all">All Participants</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2 ml-auto">
                <Switch
                  id="show-assigned"
                  checked={showAllParticipants}
                  onCheckedChange={setShowAllParticipants}
                />
                <Label htmlFor="show-assigned" className="text-sm">
                  Include already assigned
                </Label>
              </div>
            </div>
            
            {/* Capacity Warning */}
            {isAtCapacity && (
              <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg text-sm">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <span>This cohort is at capacity ({memberCount}/{capacity}). You can still add members with confirmation.</span>
              </div>
            )}
            
            {availableApplicants.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No available participants to add</p>
                <p className="text-sm mt-2">
                  {applicantFilter === 'accepted' 
                    ? 'Accept applications first or try a different filter'
                    : 'No participants match the current filter'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <Select value={selectedApplicant} onValueChange={setSelectedApplicant}>
                  <SelectTrigger data-testid="select-applicant">
                    <SelectValue placeholder="Select a participant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableApplicants.map(applicant => (
                      <SelectItem key={applicant.id} value={applicant.id}>
                        <div className="flex items-center gap-2">
                          <span>{applicant.firstName} {applicant.lastName}</span>
                          <span className="text-muted-foreground">({applicant.email})</span>
                          {applicant.applicationStatus && (
                            <Badge variant="outline" className="text-xs">
                              {applicant.applicationStatus}
                            </Badge>
                          )}
                          {applicant.isAssigned && (
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              In cohort
                            </Badge>
                          )}
                          {applicant.hasFearAssessment && applicant.hasArchetypeAssessment && (
                            <Badge variant="outline" className="text-xs bg-emerald-50">
                              Both Complete
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <p className="text-sm text-muted-foreground">
                  {availableApplicants.length} participants available
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => addMember(false)} 
              disabled={addingMember || !selectedApplicant}
              data-testid="confirm-add-member-btn"
            >
              {addingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Capacity Warning Alert Dialog */}
      <AlertDialog open={showCapacityWarning} onOpenChange={setShowCapacityWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Cohort at Capacity
            </AlertDialogTitle>
            <AlertDialogDescription>
              This cohort is already at its capacity of {capacity} members. 
              Adding more members will exceed the defined limit.
              <br /><br />
              Do you want to proceed anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingMemberAdd(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmCapacityOverride}>
              Add Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={(open) => {
        setShowEmailDialog(open);
        if (!open) resetEmailForm();
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Send Email to Cohort</DialogTitle>
            <DialogDescription>
              Send an email to all {members.length} members of this cohort
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Email Mode Toggle */}
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Switch
                  id="use-direct-email"
                  checked={useDirectEmail}
                  onCheckedChange={setUseDirectEmail}
                  data-testid="direct-email-toggle"
                />
                <Label htmlFor="use-direct-email" className="text-sm font-medium">
                  Compose Direct Email
                </Label>
              </div>
              <span className="text-xs text-muted-foreground">
                {useDirectEmail ? 'Write a custom email from scratch' : 'Use an existing template'}
              </span>
            </div>
            
            {!useDirectEmail ? (
              <>
                {/* Template Mode */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Template *</label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Subject Override <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    placeholder="Leave blank to use template subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Custom Message <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <Textarea
                    placeholder="Add a personalized message..."
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {"{{custom_message}}"} in your template to include this
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Direct Email Mode */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Subject *</label>
                  <Input
                    placeholder="Enter email subject..."
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    data-testid="direct-email-subject"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Body *</label>
                  <Textarea
                    placeholder="Write your email content here... You can use placeholders like {{first_name}}, {{last_name}}, {{cohort_name}}"
                    value={directEmailBody}
                    onChange={(e) => setDirectEmailBody(e.target.value)}
                    rows={8}
                    data-testid="direct-email-body"
                  />
                  <div className="text-xs text-muted-foreground mt-1 space-y-1">
                    <p>Available placeholders:</p>
                    <p className="font-mono">
                      {"{{first_name}}"}, {"{{last_name}}"}, {"{{email}}"}, {"{{company}}"}, {"{{cohort_name}}"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendCohortEmail} 
              disabled={sendingEmail || (!useDirectEmail && !selectedTemplate) || (useDirectEmail && (!emailSubject || !directEmailBody))}
              data-testid="send-cohort-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {members.length} Members
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Cohort Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Cohort</DialogTitle>
            <DialogDescription>
              Update cohort details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Name</label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-cohort-name"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Capacity</label>
                <Input
                  type="number"
                  min={1}
                  value={editForm.capacity || 20}
                  onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) })}
                />
                {members.length > (editForm.capacity || 20) && (
                  <p className="text-xs text-warning mt-1">
                    Current members ({members.length}) exceeds new capacity
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select 
                  value={editForm.status || 'active'} 
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <Input
                  type="date"
                  value={editForm.startDate || ''}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <Input
                  type="date"
                  value={editForm.endDate || ''}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateCohort} disabled={saving} data-testid="save-cohort-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
